// SearchService.js
// A decoupled service layer for searching across Products, Blogs, Categories, and Pages.
import { localProducts, localBlogs } from '../data/localDb';

const categoriesData = [
  {
    id: 1,
    name: "Fertilizers",
    slug: "fertilizer",
    description: "100% organic plant growth supplements that boost plant height and crop yields."
  },
  {
    id: 2,
    name: "Bio Fertilizers",
    slug: "bio-fertilizer",
    description: "Microbial inoculants that fix nitrogen and mobilize potash naturally in the soil."
  },
  {
    id: 3,
    name: "Soil Conditioners",
    slug: "soil-conditioner",
    description: "Premium earthworm castings and organic manure to enrich soil structure and carbon levels."
  },
  {
    id: 4,
    name: "Pest Control",
    slug: "pest-control",
    description: "Natural organic crop protection and biological shields against pests and pathogens."
  }
];

const pagesData = [
  {
    id: 1,
    name: "Home",
    hash: "#home",
    description: "Welcome to Orbifert: premium organic fertilizers, slideshows, and featured deals."
  },
  {
    id: 2,
    name: "About us",
    hash: "#about",
    description: "Learn about Orbifert's vision, organic certifications, NPOP compliance, and standards."
  },
  {
    id: 3,
    name: "Our Products",
    hash: "#products",
    description: "Browse our active product accordion cards, specs, reviews, and bulk purchase options."
  },
  {
    id: 4,
    name: "Shop",
    hash: "#shop",
    description: "Shop catalog list displaying all liquid, granular, and organic compost packages."
  },
  {
    id: 5,
    name: "Knowledge Hub",
    hash: "#knowledge",
    description: "Farming blogs, agricultural articles, crop research insights, and expert reviews."
  },
  {
    id: 6,
    name: "Contact",
    hash: "#contact",
    description: "Support phone numbers, emails, office address, and customer service portal."
  }
];

export const SearchService = {
  getBlogBySlug: async (slug) => {
    return localBlogs.find(b => b.slug === slug) || null;
  },

  getAllBlogs: async () => {
    return localBlogs;
  },

  getAllProducts: async () => {
    return localProducts;
  },

  search: async (query, categoryFilter = 'all') => {
    if (!query || !query.trim()) {
      return {
        products: [],
        blogs: [],
        categories: [],
        pages: []
      };
    }

    const q = query.toLowerCase().trim();

    let filteredProducts = localProducts.filter(p => 
      p.title.toLowerCase().includes(q) || 
      (p.category_name || p.category || '').toLowerCase().includes(q) ||
      (p.description || '').toLowerCase().includes(q)
    );

    if (categoryFilter !== 'all') {
      filteredProducts = filteredProducts.filter(p => {
        const cat = (p.category_name || p.category || '').toLowerCase();
        if (categoryFilter === 'fertilizer') return cat.includes('fertilizer') && !cat.includes('bio');
        if (categoryFilter === 'bio-fertilizer') return cat.includes('bio');
        if (categoryFilter === 'soil-conditioner') return cat.includes('soil');
        if (categoryFilter === 'pest-control') return cat.includes('pest') || cat.includes('disease');
        return true;
      });
    }

    const filteredBlogs = localBlogs.filter(b => 
      b.title.toLowerCase().includes(q) || 
      (b.category || '').toLowerCase().includes(q) ||
      (b.summary || '').toLowerCase().includes(q) ||
      (b.content || '').toLowerCase().includes(q)
    );

    const filteredCategories = categoriesData.filter(c => 
      c.name.toLowerCase().includes(q) ||
      c.description.toLowerCase().includes(q)
    );

    const filteredPages = pagesData.filter(p => 
      p.name.toLowerCase().includes(q) ||
      p.description.toLowerCase().includes(q)
    );

    // Normalize product shapes for search result lists
    const products = filteredProducts.map(p => ({
      id: p.id,
      title: p.title,
      slug: p.slug,
      price: parseFloat(p.starting_price ?? p.price ?? (p.variants?.[0]?.price) ?? 140.99),
      image: (Array.isArray(p.images) && p.images.length > 0)
        ? p.images[0]
        : (p.image_url || p.image || `/products/${p.title}.png`),
      category: p.category_name || p.category || 'Fertilizers',
      description: p.description,
      variants: p.variants
    }));

    const blogs = filteredBlogs.map(b => {
      const dateObj = new Date(b.created_at || Date.now());
      const day = String(dateObj.getDate());
      const monthNames = ["JAN", "FEB", "MAR", "APR", "MAY", "JUN", "JUL", "AUG", "SEP", "OCT", "NOV", "DEC"];
      const month = monthNames[dateObj.getMonth()];
      return {
        id: b.id,
        title: b.title,
        slug: b.slug,
        category: b.category,
        image: b.image_url || b.image || "https://images.unsplash.com/photo-1592982537447-6f2a6a0c7c18?w=800&auto=format&fit=crop&q=80",
        date: day,
        month: month,
        author: b.first_name ? `${b.first_name} ${b.last_name}` : 'Admin',
        commentsCount: b.comments ? b.comments.length : 0,
        summary: b.summary,
        content: b.content
      };
    });

    return {
      products: products.slice(0, 5),
      blogs: blogs.slice(0, 3),
      categories: filteredCategories.slice(0, 3),
      pages: filteredPages.slice(0, 3)
    };
  }
};

